import { memo, useCallback, useMemo } from 'react';
import { cn } from '@divlab/divanui';
import { IconDelete } from '@divlab/divanui/icons';

import Link from '@Navigation/Link';
import Img from '@UI/Img';
import styles from './ProductCard.module.css';

import type { HTMLAttributes, MouseEvent } from 'react';
import type { ProductData } from '@Types/Product';

interface Parameter {
  title: string;
  value: string;
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  product: ProductData;
  disabled?: boolean;
  onRemove?: (e: MouseEvent, product: ProductData) => void;
}

const ProductCard = (props: Props) => {
  const { product, disabled, onRemove, className, ...restProps } = props;

  const hardness: Parameter = useMemo(() => {
    const result = [];

    product.parameterGroups.forEach((parameterGroup) => {
      if (parameterGroup.theme !== 'hardness') return;

      const parameters = product.parameters.filter((targetParameter) => {
        return targetParameter.groupId === parameterGroup.id;
      });

      parameters.forEach((parameter) => {
        const parameterValue = product.parameterValues.find((pv) => {
          return pv.parameterId === parameter.id;
        });

        result.push({
          title: parameter.title,
          value: parameterValue.value[0],
        });
      });
    });

    if (result.length < 1) return null;

    return result[0];
  }, [product]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!onRemove) return;
      onRemove(e, product);
    },
    [onRemove, product],
  );

  return (
    <div {...restProps} className={cn(styles.card, { [styles.disabled]: disabled }, className)}>
      <Link to={product.link}>
        <Img className={styles.img} src={product.images[0]?.src} cnImage={styles.cnImage} />
      </Link>

      <div className={styles.container}>
        <Link to={product.link} className={styles.middleBlock}>
          <div className={styles.name}>{product.name}</div>
          {hardness && (
            <div className={styles.parameter}>
              <span className={styles.parameterTitle}>{hardness.title}: </span>
              {hardness.value}
            </div>
          )}
        </Link>

        <div className={styles.buttonDelete} onClick={handleClick}>
          <IconDelete className={styles.iconDelete} />
        </div>
      </div>
    </div>
  );
};

export default memo(ProductCard);
